/**
 * Branded type for user identity/name information. Every name field is
 * optional: an account Porter creates from a proven channel has no name until
 * its member gives one, so an all-null identity is valid and displays as
 * "Unknown". `isValid`/`tryCreate` answer whether an identity carries a name.
 * @example
 * ```typescript
 * const identity = UserIdentity.create({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   fullName: null,
 *   nickName: null,
 * });
 * ```
 */

import { z } from "zod";

export interface UserIdentityInput {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly fullName: string | null;
  readonly nickName: string | null;
}

export const UserIdentitySchema = z
  .object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    fullName: z.string().nullable(),
    nickName: z.string().nullable(),
  })
  .brand("UserIdentity");

/**
 * User identity type ensuring at least one name field is set.
 * Factory function enforces the invariant at runtime.
 */

export type UserIdentity = z.infer<typeof UserIdentitySchema>;

// Untranslated on purpose: this also feeds the admin panel and matching-engine
// panes, which have no request locale. Localising it is a wire-contract change
// (`fullName` becomes nullable), not a one-word edit.
export const UNKNOWN_PARTICIPANT_DISPLAY_NAME = "Unknown";

function isNonEmptyString(value: string | null): boolean {
  return value !== null && value.trim().length > 0;
}

/**
 * A stored string as a present value or an absent one: trimmed, or `null` when it
 * holds only whitespace. Exported because "blank" and "absent" must not mean two
 * different things across the read models that render one.
 */
export function normalizeStringOrNull(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function hasAnyName(data: UserIdentityInput): boolean {
  return (
    isNonEmptyString(data.firstName) ||
    isNonEmptyString(data.lastName) ||
    isNonEmptyString(data.fullName) ||
    isNonEmptyString(data.nickName)
  );
}

function computeDisplayName(identity: UserIdentity): string {
  if (identity.firstName !== null && identity.lastName !== null) {
    return `${identity.firstName} ${identity.lastName}`;
  }
  if (identity.firstName !== null) {
    return identity.firstName;
  }
  if (identity.lastName !== null) {
    return identity.lastName;
  }
  if (identity.fullName !== null) {
    return identity.fullName;
  }
  if (identity.nickName !== null) {
    return identity.nickName;
  }
  return UNKNOWN_PARTICIPANT_DISPLAY_NAME;
}

const nameCollator = new Intl.Collator(undefined, { sensitivity: "base" });

export const UserIdentity = {
  create(data: UserIdentityInput): UserIdentity {
    const hasFirstName = isNonEmptyString(data.firstName);
    const hasLastName = isNonEmptyString(data.lastName);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      firstName: normalizeStringOrNull(data.firstName),
      lastName: normalizeStringOrNull(data.lastName),
      // firstName+lastName and fullName are mutually exclusive representations.
      // Prefer split names: clear fullName when either is present.
      fullName:
        hasFirstName || hasLastName
          ? null
          : normalizeStringOrNull(data.fullName),
      nickName: normalizeStringOrNull(data.nickName),
    } as UserIdentity;
  },

  unsafeCreate(data: UserIdentityInput): UserIdentity {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      fullName: data.fullName,
      nickName: data.nickName,
    } as UserIdentity;
  },

  isValid(data: UserIdentityInput): boolean {
    return hasAnyName(data);
  },

  /**
   * Try to create a UserIdentity, returning undefined if invalid.
   * @param data - Identity data
   * @returns UserIdentity if valid, undefined otherwise
   */
  tryCreate(data: UserIdentityInput): UserIdentity | undefined {
    if (!hasAnyName(data)) {
      return undefined;
    }
    const hasFirstName = isNonEmptyString(data.firstName);
    const hasLastName = isNonEmptyString(data.lastName);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      firstName: normalizeStringOrNull(data.firstName),
      lastName: normalizeStringOrNull(data.lastName),
      fullName:
        hasFirstName || hasLastName
          ? null
          : normalizeStringOrNull(data.fullName),
      nickName: normalizeStringOrNull(data.nickName),
    } as UserIdentity;
  },

  /**
   * Get the best available display name for the identity.
   *
   * Priority order:
   * 1. firstName + lastName (if both provided)
   * 2. firstName (if provided)
   * 3. lastName (if provided)
   * 4. fullName (if provided)
   * 5. nickName (if provided)
   *
   * firstName/lastName are prioritized over fullName because they are
   * individually editable fields, while fullName is often a static import.
   *
   * @param identity - The identity to get display name from
   * @returns The best available display name
   */
  getDisplayName(identity: UserIdentity): string {
    return computeDisplayName(identity);
  },

  getFullName(identity: UserIdentity): string | null {
    if (identity.firstName !== null && identity.lastName !== null) {
      return `${identity.firstName} ${identity.lastName}`;
    }
    if (identity.firstName !== null) {
      return identity.firstName;
    }
    if (identity.lastName !== null) {
      return identity.lastName;
    }
    if (identity.fullName !== null) {
      return identity.fullName;
    }
    return null;
  },

  getGreetingName(identity: UserIdentity): string | undefined {
    if (identity.firstName !== null && identity.firstName !== "") {
      return identity.firstName;
    }

    if (identity.fullName !== null && identity.fullName !== "") {
      return identity.fullName;
    }

    if (identity.nickName !== null && identity.nickName !== "") {
      return identity.nickName;
    }

    if (identity.lastName !== null && identity.lastName !== "") {
      return identity.lastName;
    }
    return undefined;
  },

  compareByName(a: UserIdentity, b: UserIdentity): number {
    const lastCmp = nameCollator.compare(a.lastName ?? "", b.lastName ?? "");
    if (lastCmp !== 0) return lastCmp;

    const firstCmp = nameCollator.compare(a.firstName ?? "", b.firstName ?? "");
    if (firstCmp !== 0) return firstCmp;

    const fullCmp = nameCollator.compare(a.fullName ?? "", b.fullName ?? "");
    if (fullCmp !== 0) return fullCmp;

    return nameCollator.compare(computeDisplayName(a), computeDisplayName(b));
  },
} as const;

// Canonical placeholder identity used when scrubbing PII from an anonymized
// participant.
export const ANONYMIZED_USER_IDENTITY: UserIdentity = UserIdentity.create({
  firstName: "ANONYMIZED",
  lastName: "ANONYMIZED",
  fullName: "ANONYMIZED",
  nickName: "ANONYMIZED",
});

// An account Porter creates from a proven channel before its member gives a name.
export const NAMELESS_USER_IDENTITY: UserIdentity = UserIdentity.create({
  firstName: null,
  lastName: null,
  fullName: null,
  nickName: null,
});
